import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { emailInboxFilterStore } from "../../Stores/EmailInboxFilterStore";
import { emailInboxListStore } from "../../Stores/EmailInboxListStore";
import * as J from "@jsplumb/browser-ui";
import { BrowserJsPlumbInstance } from "@jsplumb/browser-ui";
import { Connection } from "@jsplumb/core";
import { emailInboxRuleStore } from "../../Stores/EmailInboxRuleStore";
import { Input } from "antd";
import { uniq, debounce } from "lodash";
import { modal } from "Components/General/Modal/AntdModal";

interface RulesDnDProps extends WithTranslation {
  rulesLoaded: boolean;
}

interface RulesDnDState {
  newRuleName: string;
  initialized: boolean;
}

interface RuleConnectionPayload {
  filterId: number;
  inboxId: number;
}

const DEFAULT_COLOR = "#ffa500";

export const RulesDnD = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<RulesDnDProps, RulesDnDState> {
      deletionTimeoutId: number;
      instance: BrowserJsPlumbInstance;
      containerRef = React.createRef<HTMLDivElement>();
      handleCreateNewRule = debounce(async (payload: RuleConnectionPayload) => {
        // TODO: add validation for rule name input?
        const { t } = this.props;
        const createNewRule = async () => {
          const formData: API_MailInboxRule = {
            name: this.state.newRuleName,
            filterId: payload.filterId,
            inboxes: [payload.inboxId],
          };
          const newRule = await emailInboxRuleStore.createItem(formData);
          this.addNewRule(newRule);
        };
        modal.confirm({
          title: t("Create rule"),
          content: (
            <Input
              placeholder={t("Name")}
              onChange={this.setNewRuleName}
              autoFocus={true}
              defaultValue={this.state.newRuleName}
            />
          ),
          zIndex: 1100,
          onOk: createNewRule,
          onCancel: this.resetRuleName,
        });
      }, 200);

      handleEditRule = debounce(
        async (
          rule: API_MailInboxRule,
          payload: RuleConnectionPayload,
          previousConnectionParameters: RuleConnectionPayload
        ) => {
          if (this.deletionTimeoutId) {
            window.clearTimeout(this.deletionTimeoutId);
            this.deletionTimeoutId = undefined;
          }
          try {
            let inboxes: number[];
            const previousInbox = +previousConnectionParameters.inboxId;
            // distinguish between re-assigning a connection and creating new connection
            const isEditingRule = rule.inboxes.includes(previousInbox);
            if (isEditingRule) {
              inboxes = rule.inboxes.filter((id) => id !== previousInbox);
            } else {
              inboxes = rule.inboxes;
            }
            const formData: API_MailInboxRule = {
              name: rule.name,
              filterId: payload.filterId,
              inboxes: uniq([...inboxes, payload.inboxId]),
            };
            await emailInboxRuleStore.editItem(rule.id, formData);
          } catch (error) {
            throw error;
          }
        },
        200
      );

      handleDeleteRule = debounce((payload: RuleConnectionPayload) => {
        /**
         * setTimeout is used to observe if drop triggered after detach. If no drop event was triggered -
         * this function will take care of removing inbox id from the list or deleting the rule
         */
        window.setTimeout(async () => {
          const rule = emailInboxRuleStore.data.find((item) => item.filterId === payload.filterId);
          if (!rule) {
            return;
          }
          const inboxes = rule.inboxes.filter((item) => item !== payload.inboxId);
          // if inbox list is empty after filter - delete rule, otherwise - edit
          if (inboxes.length) {
            const formData: API_MailInboxRule = {
              name: rule.name,
              filterId: payload.filterId,
              inboxes: inboxes,
            };
            await emailInboxRuleStore.editItem(rule.id, formData);
          } else {
            await emailInboxRuleStore.deleteItem(rule.id);
          }
        }, 500);
      }, 100);

      constructor(props: RulesDnDProps) {
        super(props);
        this.state = {
          newRuleName: "",
          initialized: false,
        };
      }

      setupJsPlumbInstance = () => {
        this.instance = J.newInstance({
          connector: "Straight",
          anchor: "AutoDefault",
          paintStyle: { strokeWidth: 3, stroke: DEFAULT_COLOR },
          endpoint: { type: "Dot", options: { radius: 8 } },
          endpointStyle: { fill: DEFAULT_COLOR },
          container: document.getElementById("rules-container"),
          listStyle: {
            endpoint: { type: "Rectangle", options: { width: 15, height: 15 } },
          },
        });
        this.unbindEventListeners();
        if (this.props.rulesLoaded && !this.state.initialized) {
          // used when moved from Table layout and rules are already fetched
          this.setupConnections();
          this.initEventListeners();
        }
      };

      componentDidMount() {
        setTimeout(() => {
          this.setupJsPlumbInstance();
        }, 500);
      }

      componentDidUpdate(prevProps: RulesDnDProps) {
        if (this.containerRef.current && !this.instance) {
          this.setupJsPlumbInstance();
        }
        if (this.props.rulesLoaded && !prevProps.rulesLoaded && !this.state.initialized) {
          this.setupConnections();
          this.initEventListeners();
        }
      }

      setupConnections = () => {
        const existingRules = emailInboxRuleStore.data.slice();
        const filtersListElement = document.getElementById("filters-list");
        const inboxesListElement = document.getElementById("inboxes-list");

        this.instance.batch(() => {
          this.instance.addTargetSelector("#inboxes-list .rule-col-item", {
            allowLoopback: true,
            anchor: "AutoDefault",
          });
          // this.instance.addTargetSelector("#inboxes-list .rule-col-item", {
          //   allowLoopback: true,
          //   anchor: "AutoDefault",
          // });
          this.instance.addSourceSelector("#filters-list .rule-col-item", {
            allowLoopback: true,
            anchor: "AutoDefault",
          });
          filtersListElement.querySelectorAll<HTMLDivElement>(".rule-col-item").forEach((item) => {
            this.instance.manage(item, item.dataset.payload);
          });
          inboxesListElement.querySelectorAll<HTMLDivElement>(".rule-col-item").forEach((item) => {
            this.instance.manage(item, item.dataset.payload);
          });
          existingRules.forEach((rule) => {
            const filterNode = filtersListElement.querySelector(`[data-id="${rule.filterId}"]`);
            const inboxesNodes = rule.inboxes.map((inboxId) =>
              inboxesListElement.querySelector<HTMLDivElement>(`[data-id="${inboxId}"]`)
            );
            inboxesNodes.forEach((inbox) => {
              const connection = this.instance.connect({
                source: filterNode,
                target: inbox,
                overlays: [{ type: "Label", options: { label: rule.name, cssClass: "rule-label" } }],
                // endpoint: { type: "Dot", options: { radius: 8 } },
              });
              connection.parameters.inboxId = inbox.dataset.id;
              connection.parameters.filterId = rule.filterId;
              connection.parameters.ruleId = rule.id;
            });
          });
        });
      };

      initEventListeners = () => {
        // bind connection click handler to trigger selected connection
        this.instance.bind("click", (connection, event) => {
          event.stopPropagation();
          event.preventDefault();

          /* when click on connection - paint it with different color, increase z-index to be able to select it 
        (done with css class "jtk-selected") and hide the rest overlays to be able to see label.
        */
          this.instance
            .select()
            .removeClass("jtk-selected")
            .each((con) => con.hideOverlays());
          this.instance.selectEndpoints().removeClass("jtk-selected");
          connection.showOverlays();
          connection.addClass("jtk-selected");
          connection.endpoints.forEach((endpoint) => endpoint.addClass("jtk-selected"));
          this.instance.repaintEverything();
        });
        // bind event listener that deselects connection when click outside
        document.getElementById("rules-container").addEventListener("click", this.handleDeselectConnection);

        this.instance.bind("beforeDrop", this.handleBeforeDrop);
        this.instance.bind("connection:detach", this.handleBeforeDetach);
        this.setState({ initialized: true });
      };

      handleDeselectConnection = (e: MouseEvent) => {
        if ((e.target as HTMLDivElement).id === "rules-container") {
          // remove css class for selected connection and endpoints, show all overlays
          this.instance
            .select()
            .removeClass("jtk-selected")
            .each((con) => con.showOverlays());
          this.instance.selectEndpoints().removeClass("jtk-selected");
        }
      };

      unbindEventListeners = () => {
        this.instance.unbind("click");
        this.instance.unbind("beforeDrop");
        this.instance.unbind("beforeDetach");
        document.getElementById("rules-container").removeEventListener("click", this.handleDeselectConnection);
      };

      handleBeforeDetach = ({ connection }: { connection: Connection }) => {
        const payload: RuleConnectionPayload = {
          filterId: +connection.parameters.filterId,
          inboxId: +connection.parameters.inboxId,
        };
        this.handleDeleteRule(payload);
        return true;
      };

      handleBeforeDrop = ({ connection }) => {
        const { sourceId, targetId } = connection;
        const payload = Object.assign({}, JSON.parse(sourceId), JSON.parse(targetId));
        if (!payload.filterId || !payload.inboxId) {
          return false;
        }
        const ruleFound = emailInboxRuleStore.data.find((rule) => rule.filterId === payload.filterId);
        // handle creation of new rule
        if (!ruleFound) {
          this.handleCreateNewRule(payload);
          return false;
        }
        // if inbox is already included in the rule - request is not needed as it already exists
        if (ruleFound.inboxes.includes(payload.inboxId)) {
          return true;
        }
        const parameters: RuleConnectionPayload = {
          inboxId: +connection.parameters.inboxId,
          filterId: +connection.parameters.filterId,
        };
        this.handleEditRule(ruleFound, payload, parameters);
        connection.addOverlay({ type: "Label", options: { label: ruleFound.name, cssClass: "rule-label" } });
        return true;
      };

      setNewRuleName = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ newRuleName: e.target.value });
      };

      resetRuleName = () => {
        return new Promise<void>((resolve) => {
          this.setState({ newRuleName: "" }, resolve);
        });
      };

      addNewRule = (rule: API_MailInboxRule) => {
        const filterNode = document.getElementById("filters-list").querySelector(`[data-id="${rule.filterId}"]`);
        const inboxContainer = document.getElementById("inboxes-list");
        rule.inboxes.forEach((inboxId) => {
          const inboxNode = inboxContainer.querySelector(`[data-id="${inboxId}"]`);
          try {
            const connection = this.instance.connect({
              source: filterNode,
              target: inboxNode,
              overlays: [{ type: "Label", options: { label: rule.name, cssClass: "rule-label" } }],
            });
            connection.parameters.inboxId = inboxId;
            connection.parameters.filterId = rule.filterId;
            connection.parameters.ruleId = rule.id;
          } catch (error) {
            console.log("error", error);
          }
        });
      };

      render() {
        return (
          <div id="rules-container" ref={this.containerRef}>
            <div className="rule-col right-dots" id="filters-list">
              {emailInboxFilterStore.list.map((item, idx) => (
                <div
                  className="rule-col-item"
                  key={idx}
                  data-id={item.id}
                  data-payload={JSON.stringify({ filterId: item.id })}
                >
                  <div className="rule-col-item__name">{item.name}</div>
                  <div className="rule-col-item__dragger _dragger-line" />
                </div>
              ))}
            </div>
            <div className="rule-col left-dots" id="inboxes-list">
              {emailInboxListStore.list.map((item, idx) => (
                <div
                  className="rule-col-item"
                  key={idx}
                  data-id={item.id}
                  data-payload={JSON.stringify({ inboxId: item.id })}
                >
                  <div className="rule-col-item__name">{item.name}</div>
                  <div className="rule-col-item__dragger _dragger-line" />
                </div>
              ))}
            </div>
          </div>
        );
      }
    }
  )
);
